import { ListFilterCustomSelect } from "../../components/ListFilterCustomSelect";
import { FLOW_TEXT_INPUT_BASE } from "../../styles/twDesignPrimitives";
import {
  PROPERTY_LIST_STATUS_FILTERS,
  parseCommissionTypeFilter,
  parseFurnishedFilter,
  parsePropertyListStatusFilter,
  type PropertyListAdvancedFilters,
} from "./propertiesListQuery";
import {
  PROPERTY_LIST_COMMISSION_FILTERS,
  PROPERTY_LIST_FURNISHED_FILTERS,
  buildCityFilterOptions,
  buildConsultantFilterOptions,
  buildExactCountFilterOptions,
} from "./propertyListFilterOptions";
import { PropertiesListMoneyRangeFields } from "./PropertiesListMoneyRangeFields";
import type {
  PropertyListCityOption,
  PropertyListConsultantOption,
} from "./usePropertyListFilterOptions";

type Props = {
  draft: PropertyListAdvancedFilters;
  cities: PropertyListCityOption[];
  consultants: PropertyListConsultantOption[];
  showConsultantFilter: boolean;
  hasRentRangeError: boolean;
  hasAreaRangeError: boolean;
  onChange: (next: PropertyListAdvancedFilters) => void;
  onRentRangeChange: () => void;
  onAreaRangeChange: () => void;
};

export function PropertiesListFiltersForm({
  draft,
  cities,
  consultants,
  showConsultantFilter,
  hasRentRangeError,
  hasAreaRangeError,
  onChange,
  onRentRangeChange,
  onAreaRangeChange,
}: Props) {
  const cityOptions = buildCityFilterOptions(cities, draft.city);
  const consultantOptions = buildConsultantFilterOptions(
    consultants,
    draft.consultantId
  );
  const roomOptions = buildExactCountFilterOptions(draft.rooms);
  const bathroomOptions = buildExactCountFilterOptions(draft.bathrooms);
  const garageOptions = buildExactCountFilterOptions(draft.garages, {
    includeZero: true,
    zeroLabel: "Sem vaga",
  });

  return (
    <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
      <ListFilterCustomSelect
        className="w-full min-w-0"
        label="Cidade"
        value={draft.city}
        options={cityOptions}
        onChange={(city) => onChange({ ...draft, city })}
      />
      <label className="flex flex-col gap-1 text-left text-xs font-medium text-slate-600">
        Condomínio
        <input
          className={`${FLOW_TEXT_INPUT_BASE} !font-[inherit]`}
          value={draft.condominium}
          onChange={(event) =>
            onChange({ ...draft, condominium: event.target.value })
          }
          placeholder="Ex: JSmart"
        />
      </label>
      <ListFilterCustomSelect
        className="w-full min-w-0"
        label="Status"
        value={draft.status}
        options={PROPERTY_LIST_STATUS_FILTERS}
        onChange={(status) =>
          onChange({
            ...draft,
            status: parsePropertyListStatusFilter(status),
          })
        }
      />
      <PropertiesListMoneyRangeFields
        minLabel="Aluguel mín."
        maxLabel="Aluguel máx."
        minValue={draft.minRentValue}
        maxValue={draft.maxRentValue}
        hasError={hasRentRangeError}
        onMinChange={(minRentValue) => {
          onRentRangeChange();
          onChange({ ...draft, minRentValue });
        }}
        onMaxChange={(maxRentValue) => {
          onRentRangeChange();
          onChange({ ...draft, maxRentValue });
        }}
      />
      <PropertiesListMoneyRangeFields
        minLabel="Área mín. (m²)"
        maxLabel="Área máx. (m²)"
        minValue={draft.minArea}
        maxValue={draft.maxArea}
        hasError={hasAreaRangeError}
        onMinChange={(minArea) => {
          onAreaRangeChange();
          onChange({ ...draft, minArea });
        }}
        onMaxChange={(maxArea) => {
          onAreaRangeChange();
          onChange({ ...draft, maxArea });
        }}
      />
      <ListFilterCustomSelect
        className="w-full min-w-0"
        label="Mobília"
        value={draft.isFurnished}
        options={PROPERTY_LIST_FURNISHED_FILTERS}
        onChange={(isFurnished) =>
          onChange({
            ...draft,
            isFurnished: parseFurnishedFilter(isFurnished),
          })
        }
      />
      <ListFilterCustomSelect
        className="w-full min-w-0"
        label="Tipo de comissão"
        value={draft.commissionType}
        options={PROPERTY_LIST_COMMISSION_FILTERS}
        onChange={(commissionType) =>
          onChange({
            ...draft,
            commissionType: parseCommissionTypeFilter(commissionType),
          })
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ListFilterCustomSelect
          className="w-full min-w-0"
          label="Quartos"
          value={draft.rooms}
          options={roomOptions}
          onChange={(rooms) => onChange({ ...draft, rooms })}
        />
        <ListFilterCustomSelect
          className="w-full min-w-0"
          label="Banheiros"
          value={draft.bathrooms}
          options={bathroomOptions}
          onChange={(bathrooms) => onChange({ ...draft, bathrooms })}
        />
        <ListFilterCustomSelect
          className="w-full min-w-0"
          label="Vagas"
          value={draft.garages}
          options={garageOptions}
          onChange={(garages) => onChange({ ...draft, garages })}
        />
      </div>
      {showConsultantFilter ? (
        <ListFilterCustomSelect
          className="w-full min-w-0"
          label="Consultor"
          value={draft.consultantId}
          options={consultantOptions}
          onChange={(consultantId) => onChange({ ...draft, consultantId })}
        />
      ) : null}
    </div>
  );
}
